import React, { useEffect, useState } from 'react';
import ReactApexCharts from 'react-apexcharts';
import { useSelector } from 'react-redux';
import { getAvailability } from 'api';

const AreaChart = () => {
  const [series, setSeries] = useState([]);
  const [timestamps, setTimestamps] = useState([]);

  // 🔥 Get selected date from Redux store
  const selectedDate = useSelector((state) => state?.date?.selectedDate || '2025-02-12');
  const date = useSelector((state) => state.customization.currentDate);
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const formattedDate = formatDate(date);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        if (!formattedDate) {
          console.warn('No selected date found in Redux.');
          return;
        }

        const response = await getAvailability({ date: formattedDate, source_id: 'dombivli' });

        if (!response || !response.data || response.data.length === 0) {
          console.warn('No data received for the chart.');
          setSeries([]);
          setTimestamps([]);
          return;
        }

        let availabilityData = response.data;

        const fullTimeSlots = Array.from({ length: 24 }, (_, i) => ({
          time_range: `${i}:00 - ${i}:59`,
          employee_count: 0,
          customer_count: 0
        }));

        const mergedData = fullTimeSlots.map((slot) => {
          const matchingData = availabilityData.find((item) => item.time_range === slot.time_range);
          return matchingData ? matchingData : slot;
        });

        const timeLabels = mergedData.map((item) => item.time_range);
        const employeeData = mergedData.map((item) => item.employee_count);
        const customerData = mergedData.map((item) => item.customer_count);

        console.log('Processed Time Labels:', timeLabels);
        console.log('Employee Data:', employeeData);
        console.log('Customer Data:', customerData);

        setTimestamps([...timeLabels]); // Ensure proper state update
        setSeries([
          { name: 'Employees', data: [...employeeData] },
          { name: 'Customers', data: [...customerData] }
        ]);
      } catch (error) {
        console.error('Error fetching availability data:', error);
      }
    };
    if (selectedDate) {
      fetchChartData();
    }
  }, [formattedDate, selectedDate]);
  const maxCount = series.length > 0 ? Math.max(...series.flatMap((item) => item.data), 10) : 10;

  const options = {
    chart: {
      type: 'area',
      height: 400,
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: true },
      parentHeightOffset: 0
    },
    colors: ['#44BE54', '#A8E4B1'], // ✅ Correct legend colors for Customers & Employees
    dataLabels: { enabled: false },
    stroke: {
      curve: 'smooth',
      width: 2,
      opacity: 1,
      colors: ['#44BE54', '#A8E4B1'] // ✅ Update stroke colors
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 0.6,
        opacityFrom: 0.6,
        opacityTo: 0.4,
        stops: [40, 100],
        colorStops: [
          { offset: 0, color: '#44BE54', opacity: 0.6 }, // ✅ Customers (Green)
          { offset: 100, color: '#44BE54', opacity: 0.2 },
          { offset: 0, color: '#A8E4B1', opacity: 0.6 }, // ✅ Employees (Light Green)
          { offset: 100, color: '#A8E4B1', opacity: 0.2 }
        ]
      }
    },
    legend: {
      position: 'top',
      fontSize: '12px',
      itemMargin: { horizontal: 8, vertical: 3 },
      markers: {
        fillColors: ['#44BE54', '#A8E4B1'] // ✅ Ensuring legend markers match new colors
      }
    },
    xaxis: {
      categories: timestamps.length > 0 ? timestamps : ['No Data'],
      title: { text: 'Time Range' },
      labels: { style: { fontSize: '12px' } }
    },
    yaxis: {
      min: 0,
      max: Math.ceil(maxCount * 1.1),
      tickAmount: 7,
      title: { text: 'Count' },
      labels: {
        formatter: (val) => Math.round(val),
        style: { fontSize: '12px' }
      }
    },
    tooltip: {
      shared: true,
      intersect: false,
      followCursor: true,
      marker: { show: false }
    },
    grid: {
      show: true,
      borderColor: '#f1f1f1',
      strokeDashArray: 0,
      position: 'back'
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '400px' }}>
      {series.length > 0 ? (
        <ReactApexCharts options={options} series={series} type="area" height={400} width="100%" />
      ) : (
        <p>No data available for the selected date.</p>
      )}
    </div>
  );
};

export default AreaChart;
